/*    
<summary>
  This functional component "Add Headquarter Management Form" provides form to add new headquarter or update existing headquarter.
  Developer:Mohammad Saquib Khan, Created Date:09-Apr-2025
</summary>
<returns>Returns JSX</returns>
*/

import base64 from "base-64";
import { Field, Form, Formik } from "formik";
import { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { IOption } from "../../../models/ICommon";
import LoaderButton from "../../../shared-components/button/loader-button";
import FormikFormInput from "../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormInputForRegexCheck from "../../../shared-components/FormikFormHandlers/FormikFormInputForRegexCheck";
import FormikFormSelect from "../../../shared-components/FormikFormHandlers/FormikFormSelect";
import {
  AddHeadquarterValidationSchema,
  EditHeadquarterValidationSchema,
} from "../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import { ShowHidePasswordButton } from "../../../shared-components/Grid/custom-grid-formatter";
import Label from "../../../shared-components/label/label";
import { formatMessage } from "../../../translations/format-message";
import DeletePopup from "../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../shared-components/popup/delete/delete-popup-hoc";
import { safeBase64Encode } from "../../../constants/constants";

interface IProps {
  id: number;
  isLoading: boolean;
  businessType: IOption[];
  showDeletePopup: boolean;
  submitHandler: (data: any) => void;
  onModalClosed: () => void;
  deletePopupToggleHandler: () => void;
  handleDeleteSubmitHandler: () => void;
  onDeleteModalClosed: () => void;
  initialValues: any;
}

const AddEditHeadquarterManagementForm = (props: IProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleDelete = () => {
    props.deletePopupToggleHandler();
  };

  const handleDeleteModalClosed = () => {
    props.deletePopupToggleHandler();
  };

  return (
    <>
      {props.showDeletePopup && (
        <DeletePopup
          modalSubmit={props.handleDeleteSubmitHandler}
          modalClosed={handleDeleteModalClosed}
          isLoading={false}
          title={formatMessage("delete_confirmation")}
          message={formatMessage(
            "are_you_sure_you_want_to_delete_this_headquarter"
          )}
        />
      )}
      <Modal show={true} centered>
        <Modal.Header>
          <Modal.Title>
            {props.id > 0
              ? formatMessage("headquarter_details")
              : formatMessage("headquarter_registration")}
            <button
              onClick={props.onModalClosed}
              disabled={props.isLoading}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={props.initialValues}
          enableReinitialize
          validateOnBlur={true}
          validateOnChange={true}
          validationSchema={
            props.id > 0
              ? EditHeadquarterValidationSchema
              : AddHeadquarterValidationSchema
          }
          onSubmit={async (values) => {
            let addObj = {
              HeadquarterName: values.HeadquarterName,
              BusinessType: values.BusinessTypeId,
              Email: values.Email?.toLowerCase(),
              Password: safeBase64Encode(values?.ConfirmPassword),
            };
            let updateObj = {
              Id: props.id,
              HeadquarterName: values.HeadquarterName,
              BusinessType: values.BusinessTypeId,
              Email: values.Email?.toLowerCase(),
              Password: safeBase64Encode(values?.ConfirmPassword),
            };
            props.submitHandler(props.id > 0 ? updateObj : addObj);
          }}
        >
          {({ setFieldValue, values }) => (
            <Form className="user-form">
              {props.isLoading && <FormLoader loading={props.isLoading} />}
              <Modal.Body>
                <div className="form-group mb-3 row">
                  <div className="col-sm-4 mb-3">
                    <Label
                      className="label-style"
                      required={true}
                      label="headquarter_name"
                    />
                    <Field
                      className="form-control"
                      name="HeadquarterName"
                      type="text"
                      placeholder="headquarter_name"
                      as={FormikFormInput}
                    />
                  </div>
                  <div className="col-sm-4 mb-3">
                    <Label
                      className="label-style"
                      required={true}
                      label="email"
                    />
                    <Field
                      className="form-control"
                      name="Email"
                      type="email"
                      autoComplete="new-email"
                      placeholder="enter_email"
                      validcharacter={["@", "#", "$", "-", "_", "."]}
                      as={FormikFormInput}
                      disabled={props.id > 0 ? true : false}
                    />
                  </div>
                  <div className="col-sm-4 mb-3">
                    <Label
                      className="label-style"
                      required={true}
                      label="business_type"
                    />
                    <Field
                      name="BusinessTypeId"
                      as={FormikFormSelect}
                      options={props.businessType}
                      disabled={props.id > 0 ? true : false}
                    />
                  </div>
                  {props.id < 0 && (
                    <>
                      <div className="col-sm-6 mb-3">
                        <Label
                          className="label-style"
                          required={true}
                          label="password"
                        />
                        <div className="form-group">
                          <Field
                            className="form-control"
                            name="Password"
                            autoComplete="new-password"
                            type={showPassword ? "text" : "password"}
                            placeholder="enter_new_password"
                            invalidcharacter={[" "]}
                            validcharacter={[
                              "~",
                              "`",
                              "!",
                              "@",
                              "#",
                              "$",
                              "%",
                              "^",
                              "&",
                              "*",
                              "(",
                              ")",
                              "_",
                              "-",
                              "+",
                              "=",
                              "{",
                              "[",
                              "}",
                              "]",
                              "|",
                              "\\",
                              ":",
                              ";",
                              "'",
                              '"',
                              "<",
                              ",",
                              ">",
                              ".",
                              "?",
                              "/",
                            ]}
                            as={FormikFormInput}
                          />
                          <ShowHidePasswordButton
                            showHidePassword={showPassword}
                            setShowHidePassword={setShowPassword}
                          />
                        </div>
                      </div>
                      <div className="col-sm-6 mb-3 form-group">
                        <Label
                          className="label-style"
                          required={true}
                          label="confirm_password"
                        />
                        <div className="form-group">
                          <Field
                            className="form-control"
                            autocomplete="new-password"
                            name="ConfirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="enter_confirm_password"
                            as={FormikFormInput}
                            invalidcharacter={[" "]}
                            validcharacter={[
                              "~",
                              "`",
                              "!",
                              "@",
                              "#",
                              "$",
                              "%",
                              "^",
                              "&",
                              "*",
                              "(",
                              ")",
                              "_",
                              "-",
                              "+",
                              "=",
                              "{",
                              "[",
                              "}",
                              "]",
                              "|",
                              "\\",
                              ":",
                              ";",
                              "'",
                              '"',
                              "<",
                              ",",
                              ">",
                              ".",
                              "?",
                              "/",
                            ]}
                          />
                          <ShowHidePasswordButton
                            showHidePassword={showConfirmPassword}
                            setShowHidePassword={setShowConfirmPassword}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </Modal.Body>
              {/* Footer Buttons */}
              <div className="modal-footer center-content">
                <Button
                  variant="outline-danger"
                  type="button"
                  onClick={props.onModalClosed}
                  disabled={props.isLoading}
                >
                  <i className="bi bi-x-lg me-1"></i>
                  <FormattedMessage id="close" />
                </Button>
                <LoaderButton
                  type="submit"
                  iconName="bi-check-lg"
                  isLoading={props.isLoading}
                  text={props.id > 0 ? "button_update" : "add"}
                />
                {props.id > 0 && (
                  <>
                    <Button
                      variant="danger"
                      type="button"
                      onClick={handleDelete}
                      disabled={props.isLoading}
                    >
                      <i className="bi bi-trash3-fill me-1"></i>
                      <FormattedMessage id="button_delete" />
                    </Button>
                  </>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

export default DeletePopupHOC(AddEditHeadquarterManagementForm);
